<script lang="ts" setup>
import { loadAppleAuthScript } from '@/helpers/loadExternalScript';

import AppleAuthIcon from '@/assets/images/anti-spy/auth/apple-auth-icon.svg';
import { ksEvent } from '@/plugins/awsKinesis';
import { SignUpWithApple } from '@/api/auth';

const props = withDefaults(
  defineProps<{
    kindOfPage?: string;
  }>(),
  {
    kindOfPage: 'login',
  },
);

const emit = defineEmits<{
  (event: 'change-email', value: string): void;
}>();

const changeCsrfToken = function changeCsrfToken(newToken: string) {
  const tokenMeta = document.querySelector<HTMLMetaElement>(
    '[name="csrf-token"]',
  );

  tokenMeta?.setAttribute('content', newToken);
};

const handleAppleSignIn = function handleAppleSignIn() {
  if (window.gtag) {
    window.gtag('event', `${props.kindOfPage}_page_apple_auth_selected`);
  }

  ksEvent(`${props.kindOfPage}_page_apple_auth_selected`);

  window.AppleID?.auth
    .signIn()
    .then((response) => {
      if (response.error) {
        return;
      }
      const { authorization } = response;

      return SignUpWithApple({
        token: authorization.id_token,
        code: authorization.code,
      }).then((data) => {
        emit('change-email', data.email);
        changeCsrfToken(data.csrf_token);
      });
    })
    .catch((error) => {
      if ('error' in error && error.error !== 'popup_closed_by_user') {
        throw error;
      }
    });
};

onMounted(() => {
  loadAppleAuthScript().then(() => {
    window.AppleID?.auth.init({
      clientId: 'com.veepn.login',
      scope: 'email',
      redirectURI: `https://${location.hostname}/auth/apple-callback/`,
      state: 'state',
      usePopup: true,
    });
  });
});
</script>

<template>
  <button
    id="apple-auth-button"
    dir="ltr"
    type="button"
    class="apple-auth-button"
    @click="handleAppleSignIn"
  >
    <img
      :src="AppleAuthIcon"
      alt="google auth icon"
      width="25"
      height="24"
    />
    <span>Apple</span>
  </button>
</template>

<style lang="scss" scoped>
.apple-auth-button {
  $this: 'apple-auth-button';

  position: relative;
  display: flex;
  column-gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 16px;
  outline: none;
  border: 1px solid $color-dark-blue-300;
  background: $color-white;
  border-radius: 12px;
  cursor: pointer;
  color: $color-new-black-900;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  transition: $anim-primary;

  span {
    margin: 0 auto;
  }

  &:hover {
    background-color: $color-dark-blue-100;
  }
}
</style>
